'use client';

import type { UnpackedUserToken } from '@/app_actions/Account/entities';
import { CookieService } from '@/app_lib/CookieService';
import logApiError from '@/main/lib/utils/log/logApiError';

/**
 * Checks if a user is in a paused subscription status using their Piano UID.
 *
 * Makes a POST request to the `/api/checkUserSubscriptionStatus` endpoint
 * with the given Piano UID and returns `true` if the user's subscription status
 * is 'Paused', otherwise returns `false`.
 *
 * @param {string} pianoUid - The Piano UID of the user to check.
 * @returns {Promise<boolean>} - Resolves to `true` if the user's subscription is paused, otherwise `false`.
 */
export const checkIfUserIsPaused = async (pianoUid: string): Promise<boolean> => {
  try {
    const response = await fetch('/api/checkUserSubscriptionStatus', {
      body: JSON.stringify({ pianoUid }),
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
    });

    if (!response.ok) {
      throw new Error(`Failed to check user subscription status: ${response.statusText}`);
    }
    const data = await response.json();
    return data.status === 'Paused';
  } catch (err) {
    logApiError(err as Error, '[checkIfUserIsPaused] Error checking user subscription status');
    return false;
  }
};

export const unpackAndVerifyUserTokenClient = async (token: string) => {
  const response = await fetch('/api/verifyToken', {
    body: JSON.stringify({ token }),
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'POST',
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Token verification failed: ${errorText}`);
  }

  const result = await response.json();

  if (result.error) {
    throw new Error(`Token verification error: ${result.error}`);
  }

  return result.decoded as UnpackedUserToken;
};

export const getIsLoggedInClient = async (): Promise<{
  data: null | UnpackedUserToken;
  pianoToken: string;
}> => {
  const pianoToken = CookieService.get('__utp');

  if (pianoToken) {
    try {
      const verifiedToken = await unpackAndVerifyUserTokenClient(pianoToken);
      if (verifiedToken.sub) {
        return { data: verifiedToken, pianoToken };
      }
      throw new Error('User token is invalid or missing sub');
    } catch (err) {
      // invalid token
      logApiError(err as Error, '[getIsLoggedIn] Invalid user token');
      return { data: null, pianoToken: '' };
    }
  }
  return { data: null, pianoToken: '' };
};

export const getUserAccessClient = async () => {
  try {
    const { data: verifiedToken, pianoToken } = await getIsLoggedInClient();

    if (!(verifiedToken && pianoToken)) {
      throw new Error('User token is invalid or missing sub');
    }
    CookieService.set('pianoUid', verifiedToken.sub);

    const [accessResponseResult, isPausedResult] = await Promise.allSettled([
      checkResourceAccessClient(verifiedToken.sub),
      checkIfUserIsPaused(verifiedToken.sub),
    ]);
    const accessResponse = accessResponseResult.status === 'fulfilled' ? accessResponseResult.value : null;
    const hasAccess = Boolean(accessResponse?.access?.granted);
    CookieService.set('pianoAccess', hasAccess.toString());
    const isPaused = isPausedResult.status === 'fulfilled' ? isPausedResult.value : false;
    CookieService.set('pianoPaused', isPaused.toString());

    return {
      data: verifiedToken,
      hasAccess,
      isPaused,
      loggedIn: Boolean(verifiedToken.sub),
      pianoUserToken: pianoToken,
    };
  } catch (err) {
    logApiError(err as Error, '[getUserAccess] Invalid user token');
    return {
      data: null,
      hasAccess: false,
      isPaused: false,
      loggedIn: false,
      pianoUserToken: '',
    };
  }
};

export const checkResourceAccessClient = async (userId: string) => {
  try {
    const response = await fetch('/api/checkResourceAccess', {
      body: JSON.stringify({ userId }),
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
    });

    if (!response.ok) {
      throw new Error('Check resource access failed');
    }

    const result = await response.json();

    if (result.error) {
      throw new Error(`Check resource access error: ${result.error}`);
    }

    return result.accessResponse;
  } catch (err) {
    logApiError(err as Error, '[checkResourceAccessClient] Error checking resource access');
    return null;
  }
};
